import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import OrderService from '../services/order.service';
import { Package, Truck, CreditCard, ChevronLeft, MapPin, User as UserIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatPrice } from '../utils/currency';

const OrderDetailsPage = () => {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    const TRACKING_STEPS = {
        'order_made': 'Order Placed',
        'shipped': 'Shipped',
        'reached_port': 'Arrived at Port',
        'cleared': 'Cleared Customs',
        'with_delivery_person': 'Out for Delivery',
        'delivered': 'Delivered',
        'completed': 'Completed'
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const fetchOrder = async () => {
        try {
            const response = await OrderService.getOrder(id);
            setOrder(response.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load order details");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm uppercase tracking-widest text-gray-400">Loading Order...</div>;
    if (!order) return <div className="min-h-screen flex items-center justify-center text-sm uppercase tracking-widest text-gray-400">Order not found</div>;

    const shippingAddress = order.addresses?.find(a => a.address_type === 'shipping');

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
            {/* Header */}
            <div className="mb-12">
                <Link to="/orders" className="text-xs uppercase tracking-widest text-gray-400 hover:text-black flex items-center gap-2 mb-6 transition-colors">
                    <ChevronLeft size={16} /> Back to Orders
                </Link>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <h1 className="font-serif text-3xl md:text-4xl mb-2">Order <span className="font-sans">#{order.order_number || order.id.toString().slice(0, 8)}</span></h1>
                        <p className="text-gray-500 text-sm font-light">
                            Placed on <span className="font-sans">{new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </p>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border ${order.status === 'COMPLETED' ? 'bg-green-50 text-green-700 border-green-200' :
                        order.status === 'CANCELLED' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                        }`}>
                        {order.status}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* Main Content: Items */}
                <div className="lg:col-span-2 space-y-12">
                    {/* Items List */}
                    <div className="border-t border-gray-100">
                        {order.items.map(item => (
                            <div key={item.id} className="flex gap-6 py-8 border-b border-gray-100">
                                <div className="w-20 h-24 bg-gray-50 flex-shrink-0 overflow-hidden">
                                    {item.variant_details?.product_image ? (
                                        <img src={item.variant_details.product_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-[10px] uppercase">No Img</div>
                                    )}
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-serif text-lg leading-tight mb-1">{item.variant_details?.product_name || "Product"}</h3>
                                            <p className="text-xs text-gray-400 uppercase tracking-widest">{item.variant_details?.name} {item.quantity > 1 && <span className="font-sans">x {item.quantity}</span>}</p>
                                        </div>
                                        <span className="font-medium text-sm font-sans">{formatPrice(item.price)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Financial Summary */}
                    <div className="bg-gray-50 p-8 rounded-sm space-y-3 text-sm">
                        <div className="flex justify-between text-gray-500">
                            <span>Subtotal</span>
                            <span className="font-sans">{formatPrice(order.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                            <span>Shipping</span>
                            <span className="font-sans">{formatPrice(order.shipping_cost)}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                            <span>Tax</span>
                            <span className="font-sans">{formatPrice(order.tax)}</span>
                        </div>
                        <div className="flex justify-between font-serif text-xl border-t border-gray-200 pt-4 mt-2">
                            <span>Total</span>
                            <span className="font-sans font-medium">{formatPrice(order.total)}</span>
                        </div>
                    </div>
                </div>

                {/* Sidebar: Details */}
                <div className="space-y-8">
                    {/* Shipping Address */}
                    <div className="border border-gray-100 p-8 rounded-sm">
                        <h3 className="font-serif text-xl mb-6 flex items-center gap-2">
                            <MapPin size={18} className="text-gray-400" /> Shipping
                        </h3>
                        {shippingAddress ? (
                            <div className="text-sm font-light text-gray-600 leading-relaxed">
                                <p className="font-medium text-black mb-1">{shippingAddress.street_address}</p>
                                <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}</p>
                                <p>{shippingAddress.country}</p>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 italic">No shipping address provided.</p>
                        )}
                    </div>

                    {/* Tracking Timeline */}
                    <div className="border border-gray-100 p-8 rounded-sm">
                        <h3 className="font-serif text-xl mb-6 flex items-center gap-2">
                            <Truck size={18} className="text-gray-400" /> Timeline
                        </h3>

                        <div className="relative pl-4 ml-1 space-y-8 border-l border-gray-200">
                            {order.tracking_history && order.tracking_history.length > 0 ? (
                                order.tracking_history.map((track, index) => (
                                    <div key={track.id} className="relative">
                                        <div className={`absolute -left-[21px] top-1.5 w-3 h-3 rounded-full border-2 bg-white ${index === 0 ? 'border-black' : 'border-gray-300'}`} />

                                        <div>
                                            <p className={`text-sm ${index === 0 ? 'font-bold text-black' : 'text-gray-500'}`}>
                                                {TRACKING_STEPS[track.status] || track.status}
                                            </p>
                                            <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 font-sans">
                                                {new Date(track.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                            {track.location && <p className="text-xs text-gray-500 mt-1">{track.location}</p>}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-400 italic pl-2">No tracking updates.</p>
                            )}
                        </div>
                    </div>

                    {/* Payment Info */}
                    <div className="border border-gray-100 p-8 rounded-sm">
                        <h3 className="font-serif text-xl mb-6 flex items-center gap-2">
                            <CreditCard size={18} className="text-gray-400" /> Payment
                        </h3>
                        {order.payments && order.payments.length > 0 ? (
                            <div className="space-y-4">
                                {order.payments.map((payment, idx) => (
                                    <div key={payment.id || idx} className="text-sm">
                                        <div className="flex justify-between mb-1">
                                            <span className="font-medium capitalize">
                                                {payment.payment_method === 'paystack' ? 'Online Payment' : (payment.payment_channel?.replace(/_/g, ' ') || payment.payment_method)}
                                            </span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-widest ${payment.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                                                }`}>
                                                {payment.status}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-400 font-mono font-sans">{payment.transaction_id}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 italic">No payment info.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsPage;
